import { useState, useRef, useEffect } from "react";
import { Calendar as CalendarIcon, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { cn } from "@/lib/utils";
import { formatDate } from "@/lib/api";

// Parse a YYYY-MM-DD ISO date into a local Date at midnight (no TZ shift).
function isoToDate(iso) {
    if (!iso) return undefined;
    const m = String(iso).match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (!m) return undefined;
    return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
}

// Format a JS Date as YYYY-MM-DD (local, no TZ shift).
function dateToIso(d) {
    if (!d) return "";
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    return `${yyyy}-${mm}-${dd}`;
}

/**
 * DD/MM/YYYY date picker. value / onChange are "YYYY-MM-DD" strings.
 *
 * Deliberately does NOT use Radix Popover — Radix Popover has known Safari
 * issues when nested in a Radix Dialog (portal + focus-trap collide with
 * Safari's touch-to-click delay, and clicks on the trigger inside the
 * dialog can be swallowed). We use a plain click-outside dropdown instead,
 * so it works in every browser including Safari.
 */
export function DatePicker({
    value,
    onChange,
    placeholder = "Pick a date",
    className,
    disabled,
    testId,
    allowClear = true,
}) {
    const [open, setOpen] = useState(false);
    const wrapperRef = useRef(null);
    const selected = isoToDate(value);

    // Close on outside click / touch, and on Escape.
    useEffect(() => {
        if (!open) return;
        const handlePointer = (e) => {
            if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
                setOpen(false);
            }
        };
        const handleKey = (e) => { if (e.key === "Escape") setOpen(false); };
        document.addEventListener("mousedown", handlePointer);
        document.addEventListener("touchstart", handlePointer, { passive: true });
        document.addEventListener("keydown", handleKey);
        return () => {
            document.removeEventListener("mousedown", handlePointer);
            document.removeEventListener("touchstart", handlePointer);
            document.removeEventListener("keydown", handleKey);
        };
    }, [open]);

    return (
        <div ref={wrapperRef} className={cn("relative", className)}>
            <div className="flex items-center gap-1">
                <Button
                    type="button"
                    variant="outline"
                    disabled={disabled}
                    onClick={() => setOpen((o) => !o)}
                    aria-haspopup="dialog"
                    aria-expanded={open}
                    className={cn(
                        "flex-1 justify-start text-left font-normal bg-card",
                        !selected && "text-muted-foreground",
                    )}
                    data-testid={testId}
                >
                    <CalendarIcon className="mr-2 h-4 w-4" strokeWidth={1.5} />
                    {selected ? formatDate(value) : <span>{placeholder}</span>}
                </Button>
                {selected && allowClear && !disabled && (
                    <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        onClick={() => onChange("")}
                        className="h-9 w-9 shrink-0 text-muted-foreground hover:text-destructive hover:bg-destructive/10"
                        aria-label="Clear date"
                        data-testid={testId ? `${testId}-clear` : undefined}
                    >
                        <X className="h-4 w-4" />
                    </Button>
                )}
            </div>

            {open && (
                <div
                    className="absolute z-50 mt-2 rounded-md border border-border bg-popover text-popover-foreground shadow-lg animate-in fade-in-0 zoom-in-95"
                    role="dialog"
                    data-testid={testId ? `${testId}-popover` : undefined}
                >
                    <Calendar
                        mode="single"
                        weekStartsOn={1}
                        selected={selected}
                        onSelect={(d) => {
                            onChange(d ? dateToIso(d) : "");
                            setOpen(false);
                        }}
                        initialFocus
                    />
                </div>
            )}
        </div>
    );
}

export default DatePicker;
